import styles from './sidebar-navigation.module.css';
import { Accordion } from 'radix-ui';
import { Avatar, Button, Flex, Separator, Text } from '@radix-ui/themes';
import classNames from 'classnames';
import {
  ChevronRightIcon,
  EnterIcon,
  ExitIcon,
  LightningBoltIcon
} from '@radix-ui/react-icons';
import { PERSONAL_SIDEBAR_ITEMS } from '@/app/lib/constants/navigation';
import { UserClient } from '@/app/lib/schemas/types/app-types';
import React, { ForwardedRef } from 'react';
import SidebarNavItem from './sidebar-navigation-item';
import SidebarmenuItemWithIcon from './sidebar-menu-item-with-icon';
import { BranchWithParsedCases } from '@/app/lib/schemas/types/branch-types';

interface Props {
  branches: BranchWithParsedCases[];
  pathname: string;
  user: UserClient | null;
  onClick: () => void;
  onLogin: () => void;
  onSignUp: () => void;
  onLogout: () => void;
}

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof Accordion.Trigger> {
  children: React.ReactNode;
  className?: string;
}
interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof Accordion.Content> {
  children: React.ReactNode;
  className?: string;
}

const AccordionTrigger = React.forwardRef(
  (
    { children, className, ...props }: AccordionTriggerProps,
    forwardedRef: ForwardedRef<HTMLButtonElement>
  ) => (
    <Accordion.Header className={styles.Header}>
      <Accordion.Trigger
        className={classNames(styles.Trigger, className)}
        {...props}
        ref={forwardedRef}
      >
        {children}
        <ChevronRightIcon
          className={styles.Chevron}
          aria-hidden
        />
      </Accordion.Trigger>
    </Accordion.Header>
  )
);
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef(
  (
    { children, className, ...props }: AccordionContentProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) => (
    <Accordion.Content
      className={classNames(styles.Content, className)}
      {...props}
      ref={forwardedRef}
    >
      <div className={styles.ContentText}>{children}</div>
    </Accordion.Content>
  )
);
AccordionContent.displayName = 'AccordionContent';

export default function SidebarNavigation({
  branches,
  pathname,
  user,
  onClick,
  onLogin,
  onSignUp,
  onLogout
}: Props) {
  const root = pathname.split('/');
  const slug = root.find(s => s !== '');
  const defaultValue = `/${slug}`;

  return (
    <Accordion.Root
      className={styles.Root}
      type='single'
      defaultValue={defaultValue}
      collapsible
    >
      <Flex direction='column'>
        {branches.map(branch => (
          <Accordion.Item
            key={branch.slug}
            value={`/${branch.slug}`}
            className={styles.Item}
          >
            <AccordionTrigger>{branch.name}</AccordionTrigger>
            <AccordionContent>
              <ul className={styles.Sublist}>
                {branch.topics.map(topic => (
                  <li
                    key={topic.slug}
                    className={
                      pathname.split('/').includes(topic.slug)
                        ? styles.Active
                        : undefined
                    }
                  >
                    <SidebarNavItem
                      href={`/${branch.slug}/${topic.slug}`}
                      label={topic.name}
                      isActive={pathname.split('/').includes(topic.slug)}
                      onClick={onClick}
                    />
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </Accordion.Item>
        ))}
      </Flex>
      {user ? (
        <Accordion.Item
          value='/myaccount'
          className={classNames(styles.Item, styles.AccountItem)}
        >
          <AccordionTrigger>
            <Flex
              gap='4'
              align='center'
            >
              <Avatar
                size='2'
                src={
                  user.preferences.showAvatar
                    ? user.personalData.avatar
                    : undefined
                }
                fallback={user.personalData.username.charAt(0)}
              />
              <Text>{user.personalData.username}</Text>
            </Flex>
          </AccordionTrigger>
          <AccordionContent>
            <ul className={styles.Sublist}>
              {PERSONAL_SIDEBAR_ITEMS.map(item => (
                <li
                  key={item.path}
                  className={
                    pathname.includes(item.path) ? styles.Active : undefined
                  }
                >
                  <SidebarmenuItemWithIcon
                    item={item}
                    onClick={onClick}
                  />
                </li>
              ))}
              <Separator
                orientation='horizontal'
                my='3'
                size='4'
              />
              <li>
                <Button
                  variant='ghost'
                  className={styles.Button}
                  onClick={onLogout}
                >
                  <ExitIcon /> Выйти
                </Button>
              </li>
            </ul>
          </AccordionContent>
        </Accordion.Item>
      ) : (
        <Flex
          direction='column'
          justify='end'
        >
          <Accordion.Item
            value='subscribe'
            className={classNames(styles.Item)}
          >
            <Accordion.Header className={styles.Header}>
              <Accordion.Trigger
                className={classNames(styles.Trigger, styles.Login)}
                onClick={onSignUp}
              >
                <Flex gap='4'>
                  <LightningBoltIcon /> Оформить подписку
                </Flex>
              </Accordion.Trigger>
            </Accordion.Header>
          </Accordion.Item>
          <Accordion.Item
            value='login'
            className={classNames(styles.Item)}
          >
            <Accordion.Header className={styles.Header}>
              <Accordion.Trigger
                className={classNames(styles.Trigger, styles.Login)}
                onClick={onLogin}
              >
                <Flex gap='4'>
                  <EnterIcon /> Войти
                </Flex>
              </Accordion.Trigger>
            </Accordion.Header>
          </Accordion.Item>
        </Flex>
      )}
    </Accordion.Root>
  );
}
